// src/pages/auth/ResetPassword.tsx - Using Supabase Built-in Method
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/services/supabase';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidatingToken, setIsValidatingToken] = useState<boolean>(true);
    const [isValidToken, setIsValidToken] = useState<boolean>(false);
    
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    // Password strength validation
    const [passwordStrength, setPasswordStrength] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false
    });

    useEffect(() => {
        // Validate password strength as user types
        setPasswordStrength({
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        });
    }, [password]);

    useEffect(() => {
        validateResetToken();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const validateResetToken = async () => {
        try {
            // Supabase puts the token in the URL hash after email link click
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const type = hashParams.get('type');


            // Check if this is a password recovery flow
            if (!accessToken || type !== 'recovery') {
                toast.error('Invalid or missing password reset link.');
                navigate('/request-password-reset');
                return;
            }

            // Verify the token with Supabase
            const { data: { user }, error } = await supabase.auth.getUser(accessToken);

            if (error || !user) {
                console.error('Token validation failed:', error);
                toast.error('Invalid or expired password reset link.');
                navigate('/request-password-reset');
                return;
            }

            setIsValidToken(true);
            setIsValidatingToken(false);

        } catch (error: any) {
            console.error('Token validation error:', error);
            toast.error('Failed to validate reset link.');
            navigate('/request-password-reset');
        }
    };

    const isPasswordValid = () => {
        return Object.values(passwordStrength).every(Boolean) &&
            password === confirmPassword;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isPasswordValid()) {
            toast.error('Please ensure password meets all requirements and passwords match');
            return;
        }

        setIsLoading(true);

        try {

            // Use Supabase's built-in password update method
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) {
                throw error;
            }

            toast.success('Password reset successfully! Please sign in with your new password.');
            
            // Redirect to login
            navigate('/login', { replace: true });

        } catch (error: any) {
            console.error('Password reset error:', error);
            toast.error(error.message || 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };

    // Loading state while validating token
    if (isValidatingToken) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="max-w-md w-full text-center p-6">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <h2 className="mt-4 text-xl font-semibold text-gray-900">
                        Validating Reset Link
                    </h2>
                    <p className="mt-2 text-gray-600">
                        Please wait while we verify your password reset link...
                    </p>
                </div>
            </div>
        );
    }

    // Password reset form (only show if token is valid)
    if (!isValidToken) {
        return null; // Already redirected
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                {/* Header */}
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Lock className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                        Create New Password
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Enter your new password below
                    </p>
                </div>

                {/* Password Reset Form */}
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Enter your new password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Password Strength Indicator */}
                        {password && (
                            <div className="bg-gray-50 rounded-md p-3 space-y-2">
                                <p className="text-xs font-medium text-gray-700">Password requirements:</p>
                                <ul className="space-y-1">
                                    {[
                                        { met: passwordStrength.minLength, text: 'At least 8 characters' },
                                        { met: passwordStrength.hasUpperCase, text: 'One uppercase letter' },
                                        { met: passwordStrength.hasLowerCase, text: 'One lowercase letter' },
                                        { met: passwordStrength.hasNumber, text: 'One number' },
                                        { met: passwordStrength.hasSpecialChar, text: 'One special character' }
                                    ].map((requirement, index) => (
                                        <li key={index}
                                            className={`text-xs flex items-center space-x-2 ${requirement.met ? 'text-green-600' : 'text-gray-500'}`}>
                                            <CheckCircle
                                                className={`h-4 w-4 ${requirement.met ? 'text-green-600' : 'text-gray-300'}`} />
                                            <span>{requirement.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Confirm Password Field */}
                        <div>
                            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                                Confirm Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="confirm-password"
                                    name="confirm-password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Confirm your password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                </button>
                            </div>
                            {confirmPassword && password !== confirmPassword && (
                                <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div>
                        <button
                            type="submit"
                            disabled={isLoading || !isPasswordValid()}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                         xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Resetting Password...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </div>

                    {/* Back to Login Link */}
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                            Back to login
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;